<template>
  <section
    :id="project.id"
    ref="sectionRef"
    class="project-section"
    :class="[
      project.reverse ? 'project-section--reverse' : 'project-section--normal',
      { 'is-visible': isVisible },
    ]"
  >
    <div class="project-section__wrap">
      <div class="project-section__media project-section__motion">
        <ProjectCarousel :slides="project.slides" :alt-prefix="project.title" />
      </div>

      <div class="project-section__details-wrap project-section__motion">
        <article class="project-section__details">
          <div>
            <div class="project-section__tags" :aria-label="categoryLabel">
              <span
                v-for="(tag, index) in project.tags"
                :key="tag"
                class="project-section__tag"
                :class="
                  index === 0
                    ? 'project-section__tag--green'
                    : 'project-section__tag--blue'
                "
              >
                {{ tag }}
              </span>
            </div>

            <h2>{{ project.title }}</h2>

            <div class="project-section__description">
              <p v-for="paragraph in project.paragraphs" :key="paragraph">
                {{ paragraph }}
              </p>
            </div>
          </div>

          <div class="project-section__metrics">
            <div class="project-metric project-metric--result">
              <div class="project-metric__label">
                <img src="@/assets/straithub/project/result.png" alt="" />
                <span>{{ resultLabel }}</span>
              </div>
              <strong class="project-metric__value">{{
                project.result.value
              }}</strong>
              <span class="project-metric__caption">{{
                project.result.caption
              }}</span>
            </div>

            <div class="project-metric project-metric--delivery">
              <div class="project-metric__label">
                <img src="@/assets/straithub/project/delivery.png" alt="" />
                <span>{{ deliveryLabel }}</span>
              </div>
              <div class="project-metric__status">
                <img src="@/assets/straithub/project/field.png" alt="" />
                <strong>{{ project.delivery.title }}</strong>
              </div>
              <p>{{ project.delivery.description }}</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, defineProps, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import ProjectCarousel from "./ProjectCarousel.vue";

defineProps<{
  project: {
    id: string;
    reverse?: boolean;
    tags: string[];
    title: string;
    paragraphs: string[];
    result: { value: string; caption: string };
    delivery: { title: string; description: string };
    slides: string[];
  };
}>();

const { locale } = useI18n();
const sectionRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

const resultLabel = computed(() =>
  locale.value === "zh" ? "项目成果" : "Result"
);
const deliveryLabel = computed(() =>
  locale.value === "zh" ? "项目交付" : "Delivery"
);
const categoryLabel = computed(() =>
  locale.value === "zh" ? "项目分类" : "Project categories"
);

onMounted(() => {
  if (
    typeof IntersectionObserver === "undefined" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    isVisible.value = true;
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        isVisible.value = true;
        observer?.disconnect();
      }
    },
    { threshold: 0.18 }
  );

  if (sectionRef.value) observer.observe(sectionRef.value);
});

onUnmounted(() => observer?.disconnect());
</script>

<style lang="less" scoped>
.project-section {
  position: relative;
  z-index: 0;
  scroll-margin-top: 92px;

  & + & {
    padding: 110px 0;
  }

  &:nth-child(even)::before {
    position: absolute;
    z-index: 0;
    top: -99px;
    right: 0;
    bottom: -99px;
    left: 0;
    background: linear-gradient(
      180deg,
      rgba(7, 87, 255, 0.1) 0%,
      rgba(7, 87, 255, 0) 100%
    );
    content: "";
    pointer-events: none;
  }
}

.project-section__wrap {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 45px;
  width: 1170px;
  max-width: calc(100% - 48px);
  margin: 0 auto;
}

.project-section__media,
.project-section__details-wrap {
  min-width: 0;
  height: 548px;
}

.project-section__motion {
  opacity: 0;
  backface-visibility: hidden;
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform, opacity;
}

.project-section--normal {
  .project-section__media {
    transform: translateX(-72px);
  }
  .project-section__details-wrap {
    transform: translateX(72px);
    transition-delay: 0.12s;
  }
}

.project-section--reverse {
  .project-section__media {
    order: 2;
    transform: translateX(72px);
    transition-delay: 0.12s;
  }

  .project-section__details-wrap {
    order: 1;
    transform: translateX(-72px);
  }
}

.project-section.is-visible .project-section__motion {
  opacity: 1;
  transform: translateX(0);
}

.project-section__details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 36px;
  border-radius: 15px;
  background: #ffffff;

  h2 {
    margin-top: 15px;
    color: #1c398e;
    font-family: "HarmonyOS_Bold";
    font-size: 36px;
    line-height: 45px;
    letter-spacing: 0;
  }
}

.project-section__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.project-section__tag {
  display: inline-flex;
  align-items: center;
  min-height: 19px;
  padding: 3px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-family: "HarmonyOS_Bold";
  font-size: 9px;
  line-height: 12px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.project-section__tag--green {
  border-color: #a4f4cf;
  color: #007a55;
  background: #ecfdf5;
}

.project-section__tag--blue {
  border-color: #bedbff;
  color: #1447e6;
  background: #eff6ff;
}

.project-section__description {
  margin-top: 18px;

  p {
    color: #45556c;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0;

    & + p {
      margin-top: 12px;
    }
  }
}

.project-section__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.project-metric {
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 115px;
  padding: 15px;
  border: 1px solid transparent;
  border-radius: 12px;
}

.project-metric--result {
  justify-content: space-between;
  border-color: #dbeafe;
  background: rgba(239, 246, 255, 0.7);
}

.project-metric--delivery {
  border-color: #d0fae5;
  background: rgba(236, 253, 245, 0.7);
}

.project-metric__label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #62748e;
  font-family: "HarmonyOS_Bold";
  font-size: 9px;
  line-height: 12px;
  letter-spacing: 1.6px;
  text-transform: uppercase;

  img {
    width: 12px;
    height: 12px;
    object-fit: contain;
  }
}

.project-metric__value {
  color: #1447e6;
  font-family: "Barlow Condensed", "HarmonyOS_Bold";
  font-size: 36px;
  line-height: 36px;
  white-space: nowrap;
}

.project-metric__caption {
  overflow: hidden;
  color: #45556c;
  font-family: "HarmonyOS_Bold";
  font-size: 12px;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-metric__status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: #007a55;

  img {
    width: 15px;
    height: 15px;
    object-fit: contain;
  }

  strong {
    overflow: hidden;
    font-family: "HarmonyOS_Bold";
    font-size: 12px;
    line-height: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.project-metric--delivery p {
  margin-top: 8px;
  color: #45556c;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

@media (max-width: 1180px) {
  .project-section__wrap {
    gap: 32px;
  }
  .project-section__details {
    padding: 30px;
  }
  .project-section__details h2 {
    font-size: 31px;
    line-height: 39px;
  }
}

@media (max-width: 900px) {
  .project-section {
    & + & {
      margin-top: 104px;
    }

    &:nth-child(even)::before {
      top: -52px;
      bottom: -52px;
    }
  }

  .project-section__wrap {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    max-width: calc(100% - 32px);
  }

  .project-section__media {
    order: 1 !important;
    height: min(70vw, 548px);
    min-height: 360px;
  }

  .project-section__details-wrap {
    order: 2 !important;
    height: auto;
  }

  .project-section__details {
    min-height: 500px;
  }
}

@media (max-width: 560px) {
  .project-section__media {
    min-height: 300px;
  }

  .project-section__details {
    min-height: 0;
    padding: 24px;

    h2 {
      font-size: 28px;
      line-height: 35px;
    }
  }

  .project-section__metrics {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 28px;
  }

  .project-metric {
    height: 115px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .project-section__motion {
    opacity: 1;
    transform: none !important;
    transition: none;
  }
}
</style>
